import Link from "next/link";
import { Text, HStack, UnorderedList, ListItem, Stack } from "@chakra-ui/react";
import { FiArrowUpRight } from "@onemind-services-llc/react-icons-ng/fi";
import { useTextVariants } from "@/theme/components/useSectionHeading";
import { MAIN_ROUTES, IAAS_ROUTES } from "@/constants/routes";
import baremetalImg from "public/iaas/baremetal.png";
import blockStorageImg from "public/iaas/block_storage1.png";
import dedicatedServerImg from "public/iaas/dedicated_server.png";
import disasterRecoveryImg from "public/iaas/disaster_recovery.png";
import gpuHostingImg from "public/iaas/gpu_hosting.png";
import vdiImg from "public/iaas/virtual_device_infrastructure.png";

type ContentProps = {
  points: string[];
  link: string;
};

const Content = (props: ContentProps) => {
  const { link, points } = props;
  const textVariant = useTextVariants();

  return (
    <Stack height={"100%"} justifyContent={"space-between"} spacing={4}>
      <UnorderedList>
        {points.map((list, i) => (
          <ListItem color={"text.100"} key={i}>
            <Text variant={textVariant}>{list}</Text>
          </ListItem>
        ))}
      </UnorderedList>
      <HStack as={Link} cursor={"pointer"} href={link} spacing={1}>
        <Text color={"primary.200"} fontWeight={600} variant={textVariant}>
          Explore More
        </Text>
        <FiArrowUpRight color="#1e87f0" fontSize={"24px"} />
      </HStack>
    </Stack>
  );
};

export const SERVICES_CARDS_CONSTANTS = {
  list: [
    {
      image: baremetalImg,
      title: "Bare-Metal Server ",
      content: (
        <Content
          link={`${MAIN_ROUTES.IAAS}${IAAS_ROUTES.BAREMETAL}`}
          points={[
            "Experience unparalleled performance with dedicated resources. ",
            "Direct hardware access, eliminating virtualization overhead.",
            "Ideal for HPC, large databases, and resource-intensive workloads. ",
          ]}
        />
      ),
    },
    {
      image: dedicatedServerImg,
      title: "Dedicated Servers ",
      content: (
        <Content
          link={`${MAIN_ROUTES.IAAS}${IAAS_ROUTES.DEDICATED_SERVER}`}
          points={[
            "Exclusive resources with full root and hardware access for complete control.",
            "Customizable to meet the demands of high-traffic websites and compliance-heavy applications.",
            "Ensures reliability and scalability for mission-critical workloads.",
          ]}
        />
      ),
    },
    {
      image: gpuHostingImg,
      title: "GPU Hosting ",
      content: (
        <Content
          link={`${MAIN_ROUTES.IAAS}${IAAS_ROUTES.GPU_HOSTING}`}
          points={[
            "Accelerate AI, machine learning, and data-intensive applications.",
            "GPU-optimized servers for high-speed computation and processing.",
            "Reduce time-to-insight with powerful cloud infrastructure.",
          ]}
        />
      ),
    },
    {
      image: vdiImg,
      title: "Virtual Desktop Infrastructure (VDI) ",
      content: (
        <Content
          link={`${MAIN_ROUTES.IAAS}${IAAS_ROUTES.VDI}`}
          points={[
            "Secure access to your desktop from anywhere, on any device.",
            "Centralized management, enhanced security, and seamless user experience.",
            "Boost workforce productivity with flexible cloud-based desktops.",
          ]}
        />
      ),
    },
    {
      image: blockStorageImg,
      title: "Block Storage | S3 Storage ",
      content: (
        <Content
          link={`${MAIN_ROUTES.IAAS}${IAAS_ROUTES.BLOCK_AND_S3_STORAGE}`}
          points={[
            "High-performance, low-latency storage for critical applications.",
            "Scalable object storage solutions for unstructured data.",
            "Reliable and efficient storage management tailored to your needs.",
          ]}
        />
      ),
    },
    {
      image: disasterRecoveryImg,
      title: "Disaster Recovery ",
      content: (
        <Content
          link={`${MAIN_ROUTES.IAAS}${IAAS_ROUTES.DISASTER_RECOVERY}`}
          points={[
            "Robust backup, real-time replication, and swift recovery options.",
            "Minimize downtime and data loss with tailored recovery plans.",
            "Ensure business continuity with a comprehensive DR strategy.",
          ]}
        />
      ),
    },
  ],
};
